import { useCallback, useMemo, useState } from "react";
import {
  type FilterCriteria,
  FILTER_FIELD_OPTIONS,
} from "@via/hooks/runs-filter-types.ts";

const createEmptyCriteria = (): FilterCriteria => ({
  id: crypto.randomUUID(),
  field: FILTER_FIELD_OPTIONS[0].value,
  value: "",
});

export function useRunsAdvancedFilter() {
  const [isOpen, setIsOpen] = useState(false);
  const [criteria, setCriteria] = useState<FilterCriteria[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<
    Array<{ id: string; value: string }>
  >([]);

  const openPanel = useCallback(() => {
    setIsOpen(true);
    setCriteria(prev => (prev.length === 0 ? [createEmptyCriteria()] : prev));
  }, []);

  const addCriteria = useCallback(() => {
    setCriteria(prev => [...prev, createEmptyCriteria()]);
  }, []);

  const removeCriteria = useCallback((id: string) => {
    setCriteria(prev => prev.filter(c => c.id !== id));
  }, []);

  const updateCriteria = useCallback(
    (id: string, update: Partial<Omit<FilterCriteria, "id">>) => {
      setCriteria(prev =>
        prev.map(c => {
          if (c.id !== id) return c;
          const updated = { ...c, ...update };
          if (update.field && update.field !== c.field) {
            updated.value = "";
          }
          return updated;
        })
      );
    },
    []
  );

  const applyFilters = useCallback(() => {
    const validFilters = criteria
      .filter(c => c.field && c.value.trim())
      .map(c => ({ id: c.field, value: c.value.trim() }));
    setAppliedFilters(validFilters);
  }, [criteria]);

  const clearAll = useCallback(() => {
    setCriteria([]);
    setAppliedFilters([]);
  }, []);

  const hasActiveCriteria = useMemo(
    () => criteria.some(c => c.value.trim().length > 0),
    [criteria]
  );

  return {
    isOpen,
    setIsOpen,
    openPanel,
    criteria,
    addCriteria,
    removeCriteria,
    updateCriteria,
    applyFilters,
    clearAll,
    hasActiveCriteria,
    appliedFilters,
  };
}
